import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { createSalesDocument } from "../redux/actions/salesDocumentActions";
import { fetchFiles, getFile } from "../redux/actions/fileActions";
import { fetchRelationships } from "../redux/actions/relationshipActions";
import { listTimesheets } from "../redux/actions/timesheetActions";
import { updateIsBilled } from "../redux/actions/timesheetActions";
import CustomAutocomplete from "../components/CustomAutocomplete";
import FileAutocomplete from "../components/FileAutocomplete";
import {
  Autocomplete,
  TextField,
  Button,
  MenuItem,
  Grid,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  Card,
  CardContent,
  Container,
  Stack,
  Breadcrumbs,
  Link,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  CircularProgress,
  Alert,
  FormControl
} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useNavigate, useLocation } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { textFieldStyles } from "../styles/textFieldStyles";
import SubmitButton from "../components/SubmitButton";
import CancelButton from "../components/CancelButton";
import BreadcrumbNav from "../components/BreadcrumbNav";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import format from "date-fns/esm/format";
import parse from "date-fns/esm/parse";
import enGB from "date-fns/esm/locale/en-GB";
import parseISO from "date-fns/esm/parseISO";


const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0"); // Months are zero-based
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
// Note: breadcrumbs will be updated inside the component to use t() function


const MUIDatePicker = ({ value, onChange, sx, label }) => {
  // const parsed = value ? parseISO(value) : null;
  const parsed = value ? parse(value, 'yyyy-MM-dd', new Date()) : null;

  console.log("date is", parsed)


  return (
    <FormControl variant="outlined" size="small" sx={sx}>
      <LocalizationProvider
        dateAdapter={AdapterDateFns}
        adapterLocale={enGB}
      >
        <DatePicker
          label={label}
          value={parsed}
          onChange={(newDate) => {
            if (newDate) {
              onChange(format(newDate, 'yyyy-MM-dd'));
            } else {
              onChange('');
            }
          }}
          inputFormat="dd/MM/yyyy"
          mask="__/__/____"
          slotProps={{
            textField: {
              size: 'small',
              sx: {
                // target the OutlinedInput root:
                '& .MuiOutlinedInput-root': {
                  height: 33.53,            // your desired total height
                  paddingRight: 2,       // remove extra right padding if any
                },
                // tweak the inner input padding if needed:
                '& .MuiOutlinedInput-input': {
                  padding: '8px 14px',
                },
                // shrink the calendar icon if it’s too big:
                '& .MuiInputAdornment-root svg': {
                  fontSize: '1.2rem',
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </FormControl>
  );
};











const CreateCreditPage = ({ isEditing = false, initialData = null, onSubmit = null }) => {
  const { t, ready } = useTranslation(['pages', 'forms', 'buttons', 'common', 'messages']);
  const tableStyles = windowsTableStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const locationCaseId = location.state?.caseId;
  const storedCaseId = localStorage.getItem("selectedCaseId");
  const initialCase = locationCaseId || storedCaseId || "";

  const initialFrom = location.state?.from?._id || "";
  const initialTo = location.state?.to?._id || "";

  const [formData, setFormData] = useState(
    initialData || {
      case: initialCase,
      from: initialFrom,
      to: initialTo,
      type: "credit",
      date: getCurrentDate(), // Set the current date as the initial value
      dueDate: "",
      status: "unpaid",
      items: [],
      totalDue: 0.0,
      total: 0.0,
      VAT: 0.0,
    }
  );

  const { files } = useSelector((state) => state.fileList);
  const { relationships } = useSelector((state) => state.relationshipList);
  const { timesheets } = useSelector((state) => state.timesheetList);
  const [selectedTimesheets, setSelectedTimesheets] = useState([]);
  const [filteredRelationships, setFilteredRelationships] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [isInputActive, setIsInputActive] = useState(true);
  const [showAutocomplete, setShowAutocomplete] = useState(false); // State to control visibility
  const { user } = useSelector((state) => state.userLoaded);

  const [userRole, setUserRole] = useState(null);
  const [fromFieldCleared, setFromFieldCleared] = useState(false);

  // Handle initialData updates and ensure descriptions are translated
  useEffect(() => {
    if (initialData && ready && t) {
      // Generate default items directly to avoid circular dependency
      const defaultItems = [
        { description: t('forms:billing.items.credits_on_fees'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_expenses'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_provisions_on_expenses'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_disbursements_high_vat'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_provisions_on_fees'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_provisions_on_disbursements_high_vat'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_disbursements_low_vat'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_provisions_on_disbursements_low_vat'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_disbursements_no_vat'), value: 0.0 },
        { description: t('forms:billing.items.credits_on_provisions_on_disbursements_no_vat'), value: 0.0 },
      ];

      // Check if we have valid translations
      const hasValidTranslations = defaultItems[0]?.description &&
        !defaultItems[0].description.includes('forms:billing') &&
        defaultItems[0].description.trim() !== '';

      if (hasValidTranslations) {
        console.log('Updating formData with translated descriptions for edit mode');
        setFormData(prev => ({
          ...initialData,
          items: initialData.items.map((item, index) => ({
            ...item,
            description: defaultItems[index]?.description || item.description
          }))
        }));
      }
    }
  }, [initialData, ready, t]);

  // Function to generate default credit items with current translations
  const getDefaultCreditItems = useCallback(() => [
    { description: t('forms:billing.items.credits_on_fees'), value: 0.0 },
    { description: t('forms:billing.items.credits_on_expenses'), value: 0.0 },
    { description: t('forms:billing.items.credits_on_provisions_on_expenses'), value: 0.0 },
    { description: t('forms:billing.items.credits_on_disbursements_high_vat'), value: 0.0 },
    { description: t('forms:billing.items.credits_on_provisions_on_fees'), value: 0.0 },
    {
      description: t('forms:billing.items.credits_on_provisions_on_disbursements_high_vat'),
      value: 0.0,
    },
    { description: t('forms:billing.items.credits_on_disbursements_low_vat'), value: 0.0 },
    {
      description: t('forms:billing.items.credits_on_provisions_on_disbursements_low_vat'),
      value: 0.0,
    },
    { description: t('forms:billing.items.credits_on_disbursements_no_vat'), value: 0.0 },
    {
      description: t('forms:billing.items.credits_on_provisions_on_disbursements_no_vat'),
      value: 0.0,
    },
  ], [t]);

  // Initialize and update billing items with translations
  useEffect(() => {
    const defaultItems = getDefaultCreditItems();

    // Check if we have valid translations (not just translation keys)
    const hasValidTranslations = defaultItems[0]?.description &&
      !defaultItems[0].description.includes('forms:billing') &&
      defaultItems[0].description.trim() !== '';

    console.log('Translation check:', {
      ready,
      hasValidTranslations,
      firstDescription: defaultItems[0]?.description,
      itemsLength: formData.items.length,
      isEditing,
      hasInitialData: !!initialData
    });

    if (hasValidTranslations) {
      if (!initialData) {
        // Create mode - set default items or update descriptions for language change
        if (formData.items.length === 0) {
          // Initial load - set the items
          console.log('Setting initial items for create mode');
          setFormData(prev => ({
            ...prev,
            items: defaultItems
          }));
        } else {
          // Language change - update descriptions while preserving values
          console.log('Updating item descriptions for language change in create mode');
          setFormData(prev => ({
            ...prev,
            items: prev.items.map((item, index) => ({
              ...item,
              description: defaultItems[index]?.description || item.description
            }))
          }));
        }
      } else {
        // Edit mode - update descriptions while preserving values from initialData
        console.log('Updating item descriptions for edit mode');
        setFormData(prev => ({
          ...prev,
          items: prev.items.map((item, index) => ({
            ...item,
            description: defaultItems[index]?.description || item.description
          }))
        }));
      }
    }
  }, [getDefaultCreditItems, initialData, ready, t, isEditing]);

  // Fallback initialization if translations are slow to load
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.items.length === 0 || (initialData && formData.items.some(item => item.description.includes('forms:billing')))) {
        console.log('Fallback initialization triggered for', initialData ? 'edit mode' : 'create mode');
        const defaultItems = getDefaultCreditItems();

        if (!initialData) {
          // Create mode - set default items
          setFormData(prev => ({
            ...prev,
            items: defaultItems
          }));
        } else {
          // Edit mode - update descriptions while preserving values
          setFormData(prev => ({
            ...prev,
            items: prev.items.map((item, index) => ({
              ...item,
              description: defaultItems[index]?.description || item.description
            }))
          }));
        }
      }
    }, 100); // Small delay to allow translations to load

    return () => clearTimeout(timer);
  }, [getDefaultCreditItems, initialData, formData.items]);

  useEffect(() => {
    if (user) {
      setUserRole(user.role);
    }
  }, [user]);

  // Auto-populate "From" field for all users (including Managers)
  useEffect(() => {
    if (user && relationships?.length > 0 && !formData.from && !initialFrom && !fromFieldCleared) {
      // Find the user's relationship in the relationships array
      const userRelationship = relationships?.find(
        (rel) => rel._id === user.relationship && rel.type === "Lawyer"
      );
      if (userRelationship) {
        setFormData(prev => ({ ...prev, from: userRelationship._id }));
      }
    }
  }, [user, relationships, formData.from, initialFrom, fromFieldCleared]);

  const typeOptions = [
    { label: t('forms:billing.types.invoice') },
    { label: t('forms:billing.types.credit') },
    { label: t('forms:billing.types.provision_note') },
  ];

  useEffect(() => {
    dispatch(fetchFiles());
    dispatch(fetchRelationships());
    dispatch(listTimesheets());
    console.log("Files", files);
  }, [dispatch]);


  const [breadcrumbs, setBreadcrumbs] = useState([
    { label: t('navigation:home'), href: "/" },
    { label: t('common:finance'), href: "/finance" },
  ]);

  useEffect(() => {
    const locationCaseId = location.state?.caseId;
    const initialCaseFile =
      files?.find((file) => file._id === locationCaseId) || null;

    if (initialCaseFile) {
      setBreadcrumbs((prevBreadcrumbs) => [
        ...prevBreadcrumbs,
        {
          label: initialCaseFile.name || t('common:case_file'),
          href: `/files/${initialCaseFile._id}/finance`,
        },
      ]);
    }
  }, []);


  useEffect(() => {
    if (initialCase) {
      const selectedFile = files.find((file) => file._id === initialCase);
      if (selectedFile) {
        const officeMembers = selectedFile.officeMembers.map(
          (member) => member.member
        );
        const clients = selectedFile.parties.map((member) => member.name);
        setFilteredClients(clients);
        setFilteredRelationships(officeMembers);
      }
    }
  }, [initialCase, files]);

  useEffect(() => {
    const total = formData.items.reduce((sum, item) => sum + item.value, 0);

    const VAT = formData.items.reduce((sum, item) => {
      if (item.description.includes("VAT")) {
        return sum + item.value;
      }
      return sum;
    }, 0);
    const totalDue = total;
    setFormData((prevFormData) => ({ ...prevFormData, total, VAT, totalDue }));
  }, [formData.items]);

  const { loading, success, error } = useSelector(
    (state) => state.salesDocumentCreate
  );

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (success) {
      setNotification({
        open: true,
        message: t('messages:confirmations.creditCreated'),
        severity: "success",
      });
      dispatch({ type: "SALESDOCUMENT_CREATE_RESET" }); // Reset the success state
      // navigate('/documents');
    } else if (error) {
      setNotification({
        open: true,
        message: error,
        severity: "error",
      });
      dispatch({ type: "SALESDOCUMENT_CREATE_RESET" }); // Reset the error state
    }
  }, [success, error, dispatch]);

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    const newSelectedTimesheets = timesheets
      .filter((ts) => ts.fileCase._id === formData.case)
      .map((timesheet) => {
        if (!timesheet.isBilled) {
          console.log("Timesheet ID:", timesheet._id);
          return timesheet._id;
        }
        return null; // Return null for timesheets that are billed
      })
      .filter((id) => id !== null); // Filter out null values

    setSelectedTimesheets((prevSelectedTimesheets) => {
      const uniqueTimesheets = new Set([
        ...prevSelectedTimesheets,
        ...newSelectedTimesheets,
      ]);
      return Array.from(uniqueTimesheets);
    });

    const totalFees = timesheets
      .filter((ts) => newSelectedTimesheets.includes(ts._id))
      .reduce((total, ts) => total + ts.totalAmount, 0);

    const updatedItems = formData.items.map((item) =>
      item.description === "Fees" ? { ...item, value: totalFees } : item
    );
    // console.log("updatedItems", updatedItems);
    setFormData({ ...formData, items: updatedItems });

    console.log("newSelectedTimesheets", newSelectedTimesheets);
  }, [timesheets, formData.case]);

  const handleTimesheetSelect = (timesheetId) => {
    const updatedSelectedTimesheets = selectedTimesheets.includes(timesheetId)
      ? selectedTimesheets.filter((id) => id !== timesheetId)
      : [...selectedTimesheets, timesheetId];
    console.log("updatedSelectedTimesheets", updatedSelectedTimesheets);
    setSelectedTimesheets(updatedSelectedTimesheets);

    const totalFees = timesheets
      .filter((ts) => updatedSelectedTimesheets.includes(ts._id))
      .reduce((total, ts) => total + ts.totalAmount, 0);

    const updatedItems = formData.items.map((item) =>
      item.description === "Fees" ? { ...item, value: totalFees } : item
    );
    console.log("updatedItems", updatedItems);
    setFormData({ ...formData, items: updatedItems });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCaseChange = async (event, newValue) => {
    setFormData({ ...formData, case: newValue?._id || "" });
    if (newValue) {
      const selectedFile = files.filter((file) => file._id === newValue._id)[0];
      // console.log("selectedFile", selectedFile);
      const officeMembers = selectedFile.officeMembers;
      // console.log("officeMembers", officeMembers);
      const officeMemberIds = selectedFile.officeMembers.map(
        (member) => member.member
      );
      const clients = selectedFile.parties.map((member) => member.name);
      console.log("officeMemberIds", officeMemberIds);
      // console.log("clients", clients);
      // const filtered = relationships.filter(rel => officeMemberIds.includes(rel._id));
      // console.log("filtered", filtered);
      setFilteredClients(clients);
      setFilteredRelationships(officeMemberIds);
    } else {
      setFilteredRelationships([]);
    }
  };

  const buttonStyles = { margin: "0 8px" };

  const handleCancel = () => {
    navigate("/finance");
  };

  const handleFromChange = (event, newValue) => {
    setFormData({ ...formData, from: newValue?._id || "" });
  };

  const handleToChange = (event, newValue) => {
    setFormData({ ...formData, to: newValue?._id || "" });
  };

  const handleTypeChange = (event, newValue) => {
    setFormData({ ...formData, type: newValue?.label || "" });
    // Set default items based on type
    let defaultItems = [];
    if (newValue?.label === t('forms:billing.types.invoice')) {
      defaultItems = [
        { description: t('forms:billing.items.fees'), value: 0.0 },
        { description: t('forms:billing.items.office_expenses_on_fees'), value: 0.0 },
        { description: t('forms:billing.items.expenses'), value: 0.0 },
        { description: t('forms:billing.items.high_disbursement'), value: 63.0 },
        { description: t('forms:billing.items.vat'), value: 0.0 },
      ];
    } else if (newValue?.label === t('forms:billing.types.credit')) {
      defaultItems = getDefaultCreditItems();
    } else if (newValue?.label === t('forms:billing.types.provision_note')) {
      defaultItems = [
        { description: t('forms:billing.items.provision_on_fees'), value: 0.0 },
        { description: t('forms:billing.items.provisions_on_expenses'), value: 0.0 },
        { description: t('forms:billing.items.provisions_on_disbursements_high_vat'), value: 0.0 },
        { description: t('forms:billing.items.amount_subject_to_high_vat'), value: 0.0 },
        { description: t('forms:billing.items.provisions_on_disbursements_low_vat'), value: 0.0 },
        { description: t('forms:billing.items.amount_subject_to_low_vat'), value: 0.0 },
        { description: t('forms:billing.items.provisions_on_disbursements_no_vat'), value: 0.0 },
        { description: t('forms:billing.items.amount_not_subject_to_vat'), value: 0.0 },
      ];
    }
    setFormData({ ...formData, items: defaultItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing && onSubmit) {
      onSubmit(formData);
    } else {
      dispatch(createSalesDocument(formData));
      // dispatch(updateIsBilled(selectedTimesheets, true));
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    }
  };

  // Filter files based on user role
  const filteredFiles = files.filter((file) => {
    if (userRole === "Manager") {
      return true; // Managers can see all files
    } else if (userRole === "Responsible Lawyer") {
      return file.officeMembers.some((member) => {
        // console.log('Member ID:', member.member._id);
        // console.log('User Relationship ID:', user.relationship);
        return (
          member.member._id === user.relationship &&
          member.role === "Responsible Lawyer"
        );
      });
    } else if (userRole === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) =>
          member.member._id === user.relationship &&
          member.role === "Treating Lawyer"
      );
    }
    return false;
  });

  // Add new useEffect for initialData
  useEffect(() => {
    if (isEditing && initialData && files.length > 0) {
      const caseId = initialData.case;
      const selectedFile = files.find((file) => file._id === caseId);
      if (selectedFile) {
        // Extract relationships and clients from the case file
        const officeMembers = selectedFile.officeMembers.map(
          (member) => member.member
        );
        const clients = selectedFile.parties.map((member) => member.name);

        // Set the filtered lists
        setFilteredRelationships(officeMembers);
        setFilteredClients(clients);

        console.log("Edit mode - Case ID:", caseId);
        console.log("Edit mode - Selected File:", selectedFile);
        console.log("Edit mode - Office Members:", officeMembers);
        console.log("Edit mode - Clients:", clients);
        console.log("Edit mode - formData:", formData);
      }
    }
  }, [isEditing, initialData, files]);

  // Add new useEffect to debug relationship values
  useEffect(() => {
    if (isEditing) {
      console.log("Current formData:", formData);
      console.log("Current filteredRelationships:", filteredRelationships);
      console.log("Current filteredClients:", filteredClients);
      console.log("From match:", filteredRelationships.find(rel => rel._id === formData.from));
      console.log("To match:", filteredClients.find(client => client._id === formData.to));
    }
  }, [isEditing, formData, filteredRelationships, filteredClients]);

  return (
    <Container
      maxWidth="lg"
      style={{ position: "relative", paddingBottom: "80px" }}
    >
      <BreadcrumbNav
        title={isEditing ? t('pages:finance.edit_credit') : t('pages:finance.create_credit')}
        breadcrumbs={breadcrumbs}
        showBreadcrumbs={true}
      />

      <Box
        sx={{
          backgroundColor: "white",
          padding: "30px 24px",
          marginTop: "24px",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FileAutocomplete
                options={filteredFiles}
                value={files.find((file) => file._id === formData.case) || null}
                onChange={handleCaseChange}
                label={t('forms:labels.case')}
                name="case"
              />
            </Grid>
            <Grid item xs={4}>
              <CustomAutocomplete
                options={filteredRelationships}
                value={formData.from ? (filteredRelationships.find(rel => rel._id === formData.from) || null) : null}
                onChange={handleFromChange}
                label={t('forms:labels.from')}
                filterType="Member"
                addNewLabel={t('forms:labels.add_from')}
                addNewPath="/relationships/create"
              />
            </Grid>
            <Grid item xs={4}>
              <CustomAutocomplete
                options={filteredClients}
                value={formData.to ? (filteredClients.find(client => client._id === formData.to) || null) : null}
                onChange={handleToChange}
                label={t('forms:labels.to')}
                filterType="Party"
                addNewLabel={t('forms:labels.add_to')}
                addNewPath="/relationships/create"
              />
            </Grid>

            {showAutocomplete && (
              <Grid item xs={4}>
                <Autocomplete
                  options={typeOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    typeOptions.find(
                      (option) => option.label === formData.type
                    ) || null
                  }
                  // onChange={handleTypeChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('forms:labels.type')}
                      variant="standard"
                      fullWidth
                      required
                    />
                  )}
                />
              </Grid>
            )}
            <Grid item xs={4}>

              {/* <TextField
                type="date"
                label="Date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                fullWidth
                required
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                InputLabelProps={{ shrink: true }}
              /> */}
              <MUIDatePicker
                label={t('forms:labels.date')}
                value={formData.date}
                onChange={(newValue) => setFormData({ ...formData, date: newValue })}
                sx={textFieldStyles({ width: "100%" })}
              />
            </Grid>
            <Grid item xs={4}>
              <MUIDatePicker
                label={t('forms:labels.dueDate')}
                value={formData.dueDate}
                onChange={(newValue) => setFormData({ ...formData, dueDate: newValue })}
                sx={textFieldStyles({ width: "100%" })}
              />
            
            {/* <TextField
                type="date"
                label="Due Date"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                fullWidth
                required
                sx={textFieldStyles({ width: "100%" })}
                size="small"
                InputLabelProps={{ shrink: true }}
              /> */}
          </Grid>
          <Grid item xs={12}>
            <div>
              <Typography variant="h6" sx={tableStyles.typography}>
                {t('pages:finance.billing_items')}
              </Typography>
              <TableContainer component={Paper} sx={tableStyles.container}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell sx={tableStyles.headerCell}>
                        {t('forms:labels.description')}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...tableStyles.headerCell,
                          width: "100px",
                        }}
                      >
                        {t('forms:labels.value_euro')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index} sx={tableStyles.row}>
                        <TableCell sx={tableStyles.cell}>
                          {item.description}
                        </TableCell>
                        <TableCell
                          sx={{
                            // borderBottom: "none",
                            padding: "0 8px",
                            verticalAlign: "top",
                            ...(isInputActive && {
                              "&:hover": {
                                borderBottom: "1px solid #333",
                                boxSizing: "border-box",
                                backgroundColor: "#f5f5f5", // Change this to your desired hover color
                              },
                            }),
                          }}
                        >
                          <TextField
                            type="number"
                            variant="standard"
                            name="value"
                            value={item.value}
                            onFocus={() => setIsInputActive(true)}
                            onBlur={() => setIsInputActive(false)}
                            onChange={(e) => {
                              const updatedItems = [...formData.items];
                              updatedItems[index].value = parseFloat(
                                e.target.value
                              );
                              setFormData({
                                ...formData,
                                items: updatedItems,
                              });
                            }}
                            sx={{
                              paddingLeft: "8px",
                            }}
                            fullWidth
                            InputProps={{
                              disableUnderline: true,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </Grid>
        </Grid>
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2 }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-end",
              marginRight: "0",
            }}
          >
            <Card
              sx={{
                display: "flex",
                alignItems: "center",
                paddingLeft: "5px",
                boxShadow: "none",
                border: "none",
              }}
              elevation={0}
            >
              <CardContent sx={{ padding: "8px" }}>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{ fontWeight: "bold" }}
                >
                  {t('pages:finance.due_total')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "1.2rem" }}
                >
                  € {formData.total}
                </Typography>
              </CardContent>
            </Card>
            <Card
              sx={{
                display: "flex",
                alignItems: "center",
                paddingLeft: "5px",
                boxShadow: "none",
                border: "none",
              }}
              elevation={0}
            >
              <CardContent sx={{ padding: "8px" }}>
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{ fontWeight: "bold" }}
                >
                  {t('common:vat')}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: "1.2rem" }}
                >
                  € {formData.VAT}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            // padding: "10px 16px",
            // background: "#fff",
            zIndex: 10,
            backgroundColor: "white",
            color: "#000000",
            padding: "13px 80px",
          }}
        >
          <CancelButton
            variant="outlined"
            onClick={handleCancel}
            size="small"
          >
            {t('buttons:secondary.cancel')}
          </CancelButton>
          <SubmitButton onClick={handleSubmit} size="small">
            {loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              isEditing ? t('buttons:primary.updateCredit') : t('buttons:primary.createCredit')
            )}
          </SubmitButton>
        </Box>
      </form>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
    </Container >
  );
};

export default CreateCreditPage;
